import React, { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import { register, clearRegistration, logout, getCompanyInviteDetail } from "../../../actions/auth";
import { createMessage } from "../../../actions/messages";
import RegForm from "./RegistrationSteps/RegForm";
import RegVerifyEmail from "./RegistrationSteps/RegVerifyEmail";
import RegFindCompany from "./RegistrationSteps/RegFindCompany";
import RegFixEmail from "./RegistrationSteps/RegFixEmail";
import RegCreateCompany from "./RegistrationSteps/RegCreateCompany";
import RegNotifyAdmin from "./RegistrationSteps/RegNotifyAdmin";
import Modal from "../../common/Modal/Modal";

export function Registration(props) {

    let email = "";
    if (typeof props.match.params.email !== 'undefined'){ email = props.match.params.email }

    // an admin-initiated CompanyInvite link (see People.js's "My Company" invite feature) --
    // when present, this always wins over the normal find-company/self-service-request flow
    const invite = typeof props.match.params.invite !== 'undefined' ? props.match.params.invite : null;
    const inviteDetail = useSelector(state => state.auth.inviteDetail);

    useEffect(() => {
        invite && props.getCompanyInviteDetail(invite);
    }, []);

    // set user form & company form states based on the consts below. These consts can also be used later to clear the form back to it's initial state
    const newUserInitial = {
        first_name: "",
        last_name: "",
        email: email,
        phone: "",
        password: "",
        passwordconfirm: ""
    }
    const newCompanyInitial = {
        cname: "",
        phone: "",
        website_address: "",
        street: "",
        city: "",
        state: "",
        country: "",
        industry: "",
        is_searchable: true,
        register_domain: false,
        domain: null
    }

    // LOCAL STATE --------------------------------------------------------------------------------------------------------------------------
    const [newUser, setNewUser] = useState(newUserInitial); 
    const [newCompany, setNewCompany] = useState(newCompanyInitial); 
    const [step, setStep] = useState("create user"); 
    const [searchTerm, setSearchTerm] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [revisedEmail, setRevisedEmail] = useState(null);

    // GLOBAL STATE --------------------------------------------------------------------------------------------------------------------------
    const auth = useSelector(state => state.auth);
    const status = useSelector(state => state.auth.status);


    // When changes (other than form inputs) are made, determine which modal to show according to changes made
    useEffect(() => {
        console.log("switch: " + auth.status)
        switch(auth.status) {
            case "authenticated": props.logout();
                break;
            case "registrationComplete": setStep("verify email");
                break;
            case "registering": if (!selectedCompany) {
                    setStep("find company");
                } else  {
                    selectedCompany.has_domain 
                    ? setStep("fix email")
                    : setStep("notify admin");
                }
                break;
            case "registrationFailed": props.clearRegistration();
                break;
            case "loading":
            case "loginfail":
            case "authError":
            case null: setStep("create user");
                break;
        }
    }, [auth.status, selectedCompany]);

    // As forms are filled out, update local state to match. Use "step" value to determine which state element to edit  
    const onChange = (e) => {
        if (step === "create user") setNewUser({...newUser, [e.target.id]: e.target.value});
        else if (step === "create company"){
            e.target.id === "industry"
            ?setNewCompany({...newCompany, [e.target.id]: (parseInt(e.target.value, 10))})
            :setNewCompany({...newCompany, [e.target.id]: e.target.value});
        }
        else if (step === "fix email") setRevisedEmail(e.target.value);
    }

    // When forms are submitted, grab the form information (local state) and send it to the back end in the expected format. Use "step" value to determine which state element to send
    // Once submitted successfully, (unless password mismatch prevents data sending) change step to "loading". This will be overridden once res.data is received
    const onSubmit = (e) => {
        e.preventDefault();
        if (step === "create user") {
            console.log("0001")
            // ensure that the password & password confirmation match
            if (newUser.password === newUser.passwordconfirm) {
                // an invite always skips the find-company/notify-admin dance -- the company and
                // roles were already chosen by the admin who sent it
                props.register(invite ? {...newUser, invite} : newUser);
                setStep("loading");
                // if data comes back "fail" setStep "create User"
            } else {
                props.createMessage({ passwordMismatch: "Passwords do not match." });
            }
        }
        else if (step === "create company") {
            props.register({...newUser, rc: newCompany});
            setStep("loading");
        }
        else if (step === "notify admin") {
            props.register({...newUser, coreq: true, coid: selectedCompany.id})
            setStep("loading");
        }
        else if (step === "fix email") {
            props.register({...newUser, email: (revisedEmail + "@" + selectedCompany.domain)});
            setStep("loading");
        }

    }

    // when a modal is closed, clear related global state and push to home page (which will clear this local state)
    const onCloseModal = () => {
        props.clearRegistration();
        props.history.push("/");
        // **NOTE: Future, add hasBackButton back into all these modals, and link to public site 
    }
    // if the user navigates backward to the initial user form, clear the local state & global state
    const restartRegistration = () => {
        // setNewUser(newUserInitial);
        setNewCompany(newCompanyInitial);
        searchTerm && setSearchTerm(null);
        selectedCompany && selectedCompany(null);
        revisedEmail && setRevisedEmail(null);
        props.clearRegistration();
        setStep("create user");
    }

    // an invite link with nothing to show yet (still loading) or a known-bad invite gets its
    // own minimal screens, rather than letting someone fill out the whole form first
    if (invite && inviteDetail && inviteDetail.error) {
        return (
            <Modal hasCloseButton={true} handleClose={onCloseModal} >
                <p className="content1 light-black multiline text-center">{inviteDetail.error}</p>
                <Link style={{width: "100%"}} className="btn pri gap-above-m" to="/register" >Sign up instead</Link>
            </Modal>
        );
    }
    if (invite && !inviteDetail) {
        return (
            <Modal hideLogo={true} >... Loading ...</Modal>
        );
    }

    // show the appropriate modal based on the current step
    switch (step) {
        case "create user":
        return (
            <RegForm
                onChange={onChange}
                onSubmit={onSubmit}
                isSubmitting={step === "loading"}
                newUser={newUser}
                handleClose={onCloseModal}
                inviteCompanyName={invite && inviteDetail ? inviteDetail.company_name : null}
            />
        );
        case "find company":
        return (
            <RegFindCompany 
                searchTerm={searchTerm} 
                setSearchTerm={setSearchTerm} 
                setSelectedCompany={setSelectedCompany} 
                handleClose={onCloseModal} 
                handleBack={() => restartRegistration()}
                setNewCompany={setNewCompany}
                newCompanyInitial={newCompanyInitial}
                newUser={newUser}
                setStep={setStep}
            />
        );
        case "fix email":
        return (
            <RegFixEmail
                handleBack={() => setSelectedCompany(null)}
                handleClose={onCloseModal}
                onChange={onChange}
                onSubmit={onSubmit}
                isSubmitting={step === "loading"}
                selectedCompany={selectedCompany}
            />
        );
        case "notify admin":
        return (
            <RegNotifyAdmin
                handleBack={() => setSelectedCompany(null)}
                handleClose={onCloseModal}
                selectedCompany={selectedCompany}
                onSubmit={onSubmit}
                isSubmitting={step === "loading"}
            />
        );
        case "create company":
        return (
            <RegCreateCompany
                setNewCompany={setNewCompany}
                handleClose={onCloseModal}
                handleBack={() => setStep("find company")}
                onChange={onChange}
                onSubmit={onSubmit}
                isSubmitting={step === "loading"}
                newCompany={newCompany}
                newUser={newUser}
            />
        );
        case "verify email":
        return (
            <RegVerifyEmail 
                newUser={newUser} 
                handleClose={onCloseModal}
                revisedEmail={revisedEmail}
                selectedCompany={selectedCompany}
            /> 
        );
        case "loading":
        return (
            <Modal hideLogo={true} >... Loading ...</Modal>
        );
        default:
        return (
            <></>
        );
    }
}


const mapStateToProps = () => ({});

export default connect(mapStateToProps, { register, clearRegistration, createMessage, logout, getCompanyInviteDetail })(Registration);